// Capture a canvas as a world-card thumbnail: downscale to 400px wide, JPEG,
// upload to S3 at a stable per-world key, and update worlds.thumbnail_url.
// Used by the viewer HUD (and mirrors WorldBuilder's captureAndUploadThumbnail).
import { putObject } from './uploadToStorage';
import { supabase } from '../config/supabase';

/**
 * Marks a world's thumbnail as deliberately chosen, so saving stops replacing it.
 *
 * The flag lives inside the settings jsonb, which has no partial-update API, so
 * this reads the current settings and writes them back merged. Worth the extra
 * round trip: without it, a carefully framed card is silently overwritten by
 * whatever the camera happened to be looking at on the next save.
 */
export async function pinWorldThumbnail(worldId: string): Promise<void> {
  try {
    const { data, error } = await supabase
      .from('worlds').select('settings').eq('id', worldId).single();
    if (error || !data) return;
    await supabase
      .from('worlds')
      .update({ settings: { ...(data.settings || {}), thumbnail_pinned: true } })
      .eq('id', worldId);
  } catch (err) {
    console.warn('[Thumbnail] Could not pin:', err);
  }
}

export async function captureCanvasAsWorldThumbnail(
  canvas: HTMLCanvasElement,
  worldId: string,
): Promise<string | null> {
  try {
    const thumbCanvas = document.createElement('canvas');
    const aspect = canvas.height / canvas.width;
    thumbCanvas.width = 400;
    thumbCanvas.height = Math.round(400 * aspect);
    const ctx = thumbCanvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(canvas, 0, 0, thumbCanvas.width, thumbCanvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      thumbCanvas.toBlob(resolve, 'image/jpeg', 0.8)
    );
    if (!blob) return null;

    const key = `thumbnails/${worldId}.jpg`;
    const url = (await putObject(key, blob, 'image/jpeg', { cacheControl: 'max-age=300' })) + `?t=${Date.now()}`;
    const { error } = await supabase.from('worlds').update({ thumbnail_url: url }).eq('id', worldId);
    if (error) {
      console.warn('[Thumbnail] DB update failed:', error);
      return null;
    }
    // Choosing a thumbnail by hand pins it — saves must not overwrite it.
    await pinWorldThumbnail(worldId);
    return url;
  } catch (err) {
    console.warn('[Thumbnail] Capture failed:', err);
    return null;
  }
}
